/**
 * Every permission in Walliyo (ADR-0019 §7).
 *
 * To add one:
 *   1. add it here with definePermission. Its `settingId` is
 *      <VERB_OBJECT>_STAFF_PERMISSION and it is permanent;
 *   2. give it a `name` in English, and translate that name in the console's
 *      features.<lang>.ts;
 *   3. nothing else: performAction finds it by the action.
 * Add one only where it says something the feature cannot. A feature whose
 * actions always go to the same people needs none.
 * `definitions.t.ts` checks that every permission sits on a real staff
 * feature, covers that feature's own write actions, and shares none of them.
 */

import { definePermission, type Permission } from "./types";

export const PERMISSIONS = [
  // ── Payments: maker and checker, limits and approval ────────────────────
  definePermission({
    settingId: "ISSUE_REFUND_STAFF_PERMISSION",
    name: "Issue a refund",
    feature: "ISSUE_REFUNDS_STAFF_FEATURE_ENABLEMENT",
    actions: ["ISSUE_REFUND_STAFF_ACTION"],
    category: "payments",
    sensitivity: "high",
    approvable: true,
    limit: "amount",
  }),
  // Recording that the money went out is the checker's half of a refund.
  definePermission({
    settingId: "SETTLE_REFUND_STAFF_PERMISSION",
    name: "Settle a refund",
    feature: "ISSUE_REFUNDS_STAFF_FEATURE_ENABLEMENT",
    actions: ["SETTLE_REFUND_STAFF_ACTION"],
    category: "payments",
    sensitivity: "critical",
    approvable: false,
  }),
  definePermission({
    settingId: "ISSUE_CREDIT_STAFF_PERMISSION",
    name: "Issue a credit",
    feature: "ISSUE_CREDITS_STAFF_FEATURE_ENABLEMENT",
    actions: ["ISSUE_CREDIT_STAFF_ACTION"],
    category: "payments",
    sensitivity: "high",
    approvable: true,
    limit: "amount",
  }),
  definePermission({
    settingId: "WAIVE_FEE_STAFF_PERMISSION",
    name: "Waive a fee",
    feature: "WAIVE_FEES_STAFF_FEATURE_ENABLEMENT",
    actions: ["WAIVE_FEE_STAFF_ACTION"],
    category: "payments",
    sensitivity: "high",
    approvable: true,
    limit: "amount",
  }),
  // ── Support: a trainee claims, notes and logs calls under the feature
  //    alone, but does not speak for Walliyo or close a case ──────────────
  definePermission({
    settingId: "REPLY_TO_CUSTOMER_STAFF_PERMISSION",
    name: "Reply to a customer",
    feature: "CLAIM_CASES_STAFF_FEATURE_ENABLEMENT",
    actions: ["REPLY_TO_CASE_STAFF_ACTION"],
    category: "support",
    sensitivity: "low",
    approvable: false,
  }),
  definePermission({
    settingId: "CLOSE_CASE_STAFF_PERMISSION",
    name: "Close a case",
    feature: "CLAIM_CASES_STAFF_FEATURE_ENABLEMENT",
    actions: ["CLOSE_CASE_STAFF_ACTION"],
    category: "support",
    sensitivity: "low",
    approvable: false,
  }),
] as const satisfies readonly Permission[];

export type DefinedPermissionId = (typeof PERMISSIONS)[number]["settingId"];

/** Every limit a permission can carry, as the settings they are. */
export const PERMISSION_LIMITS = PERMISSIONS.flatMap((permission) => (permission.limit ? [permission.limit] : []));

export type DefinedPermissionLimitId = (typeof PERMISSION_LIMITS)[number]["settingId"];

const BY_ACTION: ReadonlyMap<string, Permission> = new Map(
  PERMISSIONS.filter((permission) => permission.status !== "retired").flatMap((permission) =>
    permission.actions.map((actionId) => [actionId, permission] as const),
  ),
);

const BY_ID: ReadonlyMap<string, Permission> = new Map(
  PERMISSIONS.map((permission) => [permission.settingId, permission] as const),
);

/** The permission that covers an action, or null when its feature alone gates it. */
export function permissionOfAction(actionId: string): Permission | null {
  return BY_ACTION.get(actionId) ?? null;
}

export function findPermission(settingId: string): Permission | null {
  return BY_ID.get(settingId) ?? null;
}

/** The permissions that sit on one feature, in the order they are defined. */
export function permissionsUnder(featureId: string): readonly Permission[] {
  return PERMISSIONS.filter((permission) => permission.feature === featureId && permission.status !== "retired");
}
